/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import type React from 'react';

import type { MappingRowProps, RowLayerAction, TemplateRevert } from './MappingRow';
import type { RowLayerActionKind } from '../../../utils/artifact-inheritance';
import type { ValueOrigin } from './parameter-editor.adapters';

/** What a surface knows about where a row's value came from: whose it is, or how it was authored. */
export interface RowOriginAnswer {
  /** The inheritance mark, where the artifact has a parent with an opinion about this row. */
  inheritance?: React.ReactNode;
  /** Otherwise the row's authoring origin. */
  origin?: ValueOrigin;
}

/**
 * **Inheritance wins where it has an opinion, and otherwise the row says how it was authored** — once.
 *
 * This sentence was a comment in three mark builders (parameters, headers, the ORTB body). `#1001`
 * gave the provenance half a way back, and a way back is something every surface must answer — so
 * the one-or-other rule and the answer travel together here rather than being restated per editor.
 *
 * `onRevert` is **required and nullable**: `null` is a surface with no detected mappings to revert, or
 * a viewer who may not change the artifact.
 */
export function originMarks(
  answer: RowOriginAnswer,
  onRevert: (() => void) | null,
  /** The way back to a template's current row (Todd, 2026-09-24) — `null` where there is none. */
  templateRevert: TemplateRevert | null,
): Pick<NonNullable<MappingRowProps['marks']>, 'inheritance' | 'provenance'> {
  return answer.inheritance
    ? { inheritance: answer.inheritance }
    : { provenance: { origin: answer.origin, onRevert, templateRevert } };
}

/**
 * **A row's layer action, bound to its editor's two gestures** (`#1126`, `X31`).
 *
 * The *which* is `rowLayerActionKind`, shared; this is the *how*, also shared because every editor
 * has exactly two things it can do to a row — drop it, or remove the parent's row from this source.
 * `null` passes through: a row with no action renders none.
 */
export function layerActionFor(
  kind: RowLayerActionKind | null,
  subject: string,
  act: { remove: () => void; stopOverriding: (() => void) | null },
): RowLayerAction | null {
  if (!kind) return null;
  if (kind === 'remove') return { kind, subject, onAct: act.remove };
  return act.stopOverriding ? { kind, subject, onAct: act.stopOverriding } : null;
}
